import './App.css';
import { useState, useEffect } from 'react';
import { Facebook, Instagram } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Hook para animaciones de scroll
const useScrollAnimation = () => {
  useEffect(() => {
    // Pequeño delay para asegurar que el DOM está completamente renderizado
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      const selectorsToObserve = [
        'section h2',
        'section h3',
        '.about-card',
        '.product-card',
        '.feature-card',
        '.about-highlight',
        'section p:not(.hero-subtitle)',
        '.hero-badge',
        '.split-chars',
        '.hero-subtitle',
        '.cta-button'
      ];

      selectorsToObserve.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          el.classList.add('animate-on-scroll');
          
          // Si el elemento ya está visible en el viewport, agregar clase visible inmediatamente
          const rect = el.getBoundingClientRect();
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            el.classList.add('visible');
          } else {
            observer.observe(el);
          }
        });
      });

      return () => observer.disconnect();
    }, 100);

    return () => clearTimeout(timer);
  }, []);
};

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedRoutine, setSelectedRoutine] = useState(null);

  // Aplicar animaciones de scroll
  useScrollAnimation();

  useEffect(() => {
    if (selectedRoutine) {
      window.scrollTo(0, 0);
    }
  }, [selectedRoutine]);

  // Animaciones GSAP Simples y Robustas
  useEffect(() => {
    if (!selectedRoutine) {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());

      setTimeout(() => {
        // Animaciones del hero - usando .to() en lugar de .from()
        gsap.to('.hero-badge', {
          duration: 0.8,
          opacity: 1,
          y: 0,
          ease: 'back.out(1.7)',
          delay: 0.1
        });

        gsap.to('.split-chars span', {
          duration: 0.8,
          opacity: 1,
          y: 0,
          rotationX: 0,
          stagger: 0.05,
          ease: 'back.out(1.5)',
          delay: 0.3
        });

        gsap.to('.hero-subtitle', {
          duration: 0.8,
          opacity: 1,
          y: 0,
          ease: 'power2.out',
          delay: 0.8
        });

        gsap.to('.cta-button', {
          duration: 0.6,
          opacity: 1,
          y: 0,
          scale: 1,
          stagger: 0.1,
          ease: 'elastic.out(1, 0.4)',
          delay: 1
        });

        // Elementos circulares animados
        gsap.to('.element-circle', {
          duration: 15,
          x: (i) => (i % 2 === 0 ? 100 : -100),
          y: (i) => (i % 2 === 0 ? -80 : 80),
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          stagger: {amount: 1.5, from: 'start'}
        });

        // Scroll indicator
        gsap.to('.scroll-icon', {
          duration: 1.5,
          y: 10,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut'
        });

        // Parallax mouse
        const handleMouseMove = (e) => {
          const x = e.clientX / window.innerWidth - 0.5;
          const y = e.clientY / window.innerHeight - 0.5;

          gsap.to('.element-circle', {
            duration: 1,
            x: (i) => x * 40 * (i + 1),
            y: (i) => y * 40 * (i + 1),
            overwrite: 'auto'
          });
        };

        window.addEventListener('mousemove', handleMouseMove);

        // Scroll triggered animations para cards
        gsap.utils.toArray('.about-card').forEach((card) => {
          gsap.from(card, {
            scrollTrigger: {trigger: card, start: 'top 75%'},
            duration: 0.6,
            opacity: 0,
            y: 20,
            ease: 'power2.out'
          });
        });

        gsap.utils.toArray('.product-card').forEach((card) => {
          gsap.from(card, {
            scrollTrigger: {trigger: card, start: 'top 75%'},
            duration: 0.6,
            opacity: 0,
            y: 20,
            ease: 'power2.out'
          });
        });

        gsap.utils.toArray('.feature-card').forEach((card) => {
          gsap.from(card, {
            scrollTrigger: {trigger: card, start: 'top 75%'},
            duration: 0.6,
            opacity: 0,
            y: 20,
            ease: 'power2.out'
          });
        });

        return () => {
          window.removeEventListener('mousemove', handleMouseMove);
          ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
      }, 100);

      return () => {
        ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      };
    }
  }, [selectedRoutine]);

  // Efecto hover en tarjetas
  const handleCardHover = (e, isEnter) => {
    if (isEnter) {
      gsap.to(e.currentTarget, {
        duration: 0.3,
        y: -10,
        boxShadow: '0 20px 40px rgba(255, 107, 53, 0.3)',
        ease: 'power2.out'
      });
    } else {
      gsap.to(e.currentTarget, {
        duration: 0.3,
        y: 0,
        boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)',
        ease: 'power2.out'
      });
    }
  };

  // Efecto hover en botones
  const handleButtonHover = (e, isEnter) => {
    if (isEnter) {
      gsap.to(e.currentTarget, {
        duration: 0.3,
        scale: 1.05,
        ease: 'power2.out'
      });
    } else {
      gsap.to(e.currentTarget, {
        duration: 0.3,
        scale: 1,
        ease: 'power2.out'
      });
    }
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const routineDetails = {
    'hombres-principiante': {
      title: 'RUTINA PARA HOMBRES - NIVEL PRINCIPIANTE',
      duration: '4 SEMANAS',
      price: '$50.000,00',
      badge: 'Principiante',
      description: 'Rutina diseñada para hombres que recién comienzan su entrenamiento. Se enfoca en aprender la técnica correcta y construir una base sólida durante 4 semanas.',
      exercises: [
        { name: 'Flexiones de pecho', reps: '3 x 10', explanation: 'Colócate en posición de plank, baja el cuerpo hasta tocar el pecho al piso, sube controladamente', videoUrl: 'https://www.youtube.com/embed/IODxDxX7oi4' },
        { name: 'Press de banca con mancuernas', reps: '3 x 12', explanation: 'Acostado en banco, brazos en 90 grados, presiona hacia arriba', videoUrl: 'https://www.youtube.com/embed/pJ0z6uu0UM4' },
        { name: 'Dominadas', reps: '3 x 8', explanation: 'Agárrate de la barra, sube hasta que tu barbilla supere la barra', videoUrl: 'https://www.youtube.com/embed/d6V4wnVZS38' },
        { name: 'Curls de bíceps', reps: '3 x 12', explanation: 'De pie, con mancuernas, flexiona los codos llevando las mancuernas hacia los hombros', videoUrl: 'https://www.youtube.com/embed/rm2Rnq9XNxg' },
        { name: 'Sentadillas', reps: '3 x 15', explanation: 'Baja como si quisieras sentarte, manteniendo el pecho arriba y rodillas alineadas', videoUrl: 'https://www.youtube.com/embed/xqvCmoLULNY' },
        { name: 'Estocadas', reps: '3 x 12', explanation: 'Da un paso adelante, baja hasta flexionar ambas rodillas a 90 grados', videoUrl: 'https://www.youtube.com/embed/QOVaHwm-Q6U' }
      ]
    },
    'mujeres-principiante': {
      title: 'RUTINA PARA MUJERES - NIVEL PRINCIPIANTE',
      duration: '4 SEMANAS',
      price: '$50.000,00',
      badge: 'Principiante',
      description: 'Rutina diseñada para mujeres que recién comienzan. Tonificación, resistencia y construcción de confianza con ejercicios accesibles durante 4 semanas.',
      exercises: [
        { name: 'Flexiones en pared', reps: '3 x 10', explanation: 'De frente a la pared, brazos extendidos, flexiona los codos bajando el cuerpo', videoUrl: 'https://www.youtube.com/embed/IODxDxX7oi4' },
        { name: 'Press de hombros', reps: '3 x 12', explanation: 'Con mancuernas, presiona hacia arriba desde los hombros', videoUrl: 'https://www.youtube.com/embed/qEwKkZR63fY' },
        { name: 'Burpees', reps: '3 x 8', explanation: 'De pie, flexiona, salta hacia atrás, flexión, salta hacia adelante', videoUrl: 'https://www.youtube.com/embed/TU2atWxl-5E' },
        { name: 'Planchas', reps: '3 x 30seg', explanation: 'Posición de plank, mantén el cuerpo recto', videoUrl: 'https://www.youtube.com/embed/pSHjTRCQxIw' },
        { name: 'Sentadillas con peso corporal', reps: '3 x 15', explanation: 'Baja controladamente, peso en los talones', videoUrl: 'https://www.youtube.com/embed/xqvCmoLULNY' },
        { name: 'Glute bridges', reps: '3 x 15', explanation: 'Acostada boca arriba, levanta la cadera presionando glúteos', videoUrl: 'https://www.youtube.com/embed/wPM8icgrkY4' }
      ]
    },
    'mujeres-intermedio': {
      title: 'RUTINA PARA MUJERES - NIVEL INTERMEDIO',
      duration: '4 SEMANAS',
      price: '$50.000,00',
      badge: 'Intermedio',
      description: 'Rutina intermedia para mujeres con experiencia. Mayor intensidad y variedad de ejercicios durante 4 semanas.',
      exercises: [
        { name: 'Flexiones en piso', reps: '4 x 12', explanation: 'Control total del movimiento, baja hasta casi tocar el piso', videoUrl: 'https://www.youtube.com/embed/IODxDxX7oi4' },
        { name: 'Remo con mancuernas', reps: '4 x 12', explanation: 'Inclina el torso, levanta mancuernas hacia la cadera', videoUrl: 'https://www.youtube.com/embed/wqe1ctznW0M' }
      ]
    },
    'hombres-intermedio': {
      title: 'RUTINA PARA HOMBRES - NIVEL INTERMEDIO',
      duration: '4 SEMANAS',
      price: '$50.000,00',
      badge: 'Intermedio',
      description: 'Rutina intermedia para hombres. Aumento de intensidad y mayor variedad de estímulos durante 4 semanas.',
      exercises: [
        { name: 'Press de banca', reps: '4 x 8', explanation: 'Control del peso, explosividad en la subida', videoUrl: 'https://www.youtube.com/embed/pJ0z6uu0UM4' },
        { name: 'Press de hombros', reps: '4 x 10', explanation: 'Presiona desde los hombros hacia arriba', videoUrl: 'https://www.youtube.com/embed/qEwKkZR63fY' }
      ]
    },
    'hombres-avanzado': {
      title: 'RUTINA PARA HOMBRES - NIVEL AVANZADO',
      duration: '4 SEMANAS',
      price: '$50.000,00',
      badge: 'Avanzado',
      description: 'Rutina avanzada para hombres con experiencia. Máxima intensidad y técnica refinada durante 4 semanas.',
      exercises: [
        { name: 'Press de banca con barra', reps: '5 x 5', explanation: 'Peso pesado, controla la bajada', videoUrl: 'https://www.youtube.com/embed/pJ0z6uu0UM4' },
        { name: 'Fondos', reps: '4 x 8-10', explanation: 'Ejercicio avanzado para tríceps', videoUrl: 'https://www.youtube.com/embed/6kALZikXxNA' }
      ]
    },
    'mujeres-avanzado': {
      title: 'RUTINA PARA MUJERES - NIVEL AVANZADO',
      duration: '4 SEMANAS',
      price: '$50.000,00',
      badge: 'Avanzado',
      description: 'Rutina avanzada para mujeres. Máxima intensidad y técnica refinada durante 4 semanas.',
      exercises: [
        { name: 'Press de banca', reps: '5 x 5', explanation: 'Trabajo con peso pesado', videoUrl: 'https://www.youtube.com/embed/pJ0z6uu0UM4' },
        { name: 'Dominadas con peso', reps: '4 x 6-8', explanation: 'Dominadas con cinturón de peso', videoUrl: 'https://www.youtube.com/embed/d6V4wnVZS38' }
      ]
    },
    'lesion-rodilla': {
      title: 'RUTINA ENTRENAMIENTO - LESIONES DE RODILLA',
      duration: '4 SEMANAS',
      price: '$60.000,00',
      badge: 'Especializado',
      description: 'Rutina especializada para rehabilitación y fortalecimiento sin impacto en rodillas lesionadas durante 4 semanas.',
      exercises: [
        { name: 'Stretching de cuádriceps', reps: '3 x 30seg', explanation: 'Estira suavemente los músculos frontales del muslo', videoUrl: 'https://www.youtube.com/embed/L9t-EGJ3a0c' },
        { name: 'Elevaciones de pierna acostado', reps: '3 x 15', explanation: 'Sin flexionar la rodilla, levanta la pierna', videoUrl: 'https://www.youtube.com/embed/VnCvX-fFe5w' }
      ]
    },
    'dolor-lumbar': {
      title: 'RUTINA DE ENTRENAMIENTO - DOLOR LUMBAR',
      duration: '4 SEMANAS',
      price: '$60.000,00',
      badge: 'Especializado',
      description: 'Rutina especializada para aliviar y prevenir dolor lumbar fortaleciendo core y espalda baja durante 4 semanas.',
      exercises: [
        { name: 'Planchas abdominales', reps: '3 x 45seg', explanation: 'Mantén el cuerpo recto, contrae el core', videoUrl: 'https://www.youtube.com/embed/pSHjTRCQxIw' },
        { name: 'Superman', reps: '3 x 12', explanation: 'Acostado boca abajo, levanta brazos y piernas', videoUrl: 'https://www.youtube.com/embed/SLDqQQqLLx8' }
      ]
    }
  };

  if (selectedRoutine) {
    return (
      <div className="app routine-page">
        <section className="routine-detail">
          <button className="back-button" onClick={() => setSelectedRoutine(null)}>← Volver a Destacados</button>
          <div className="routine-header">
            <span className="routine-badge">{selectedRoutine.badge}</span>
            <h2>{selectedRoutine.title}</h2>
            <p className="routine-duration">{selectedRoutine.duration}</p>
            <p className="routine-description">{selectedRoutine.description}</p>
          </div>

          <div className="routine-content">
            <h3 className="routine-exercises-title">Ejercicios de la Rutina</h3>
            <div className="exercises-list">
              {selectedRoutine.exercises && selectedRoutine.exercises.map((exercise, exIndex) => (
                <div key={exIndex} className="exercise-item">
                  <div className="exercise-info">
                    <h4>{exercise.name}</h4>
                    <p className="reps-info"><strong>Series:</strong> {exercise.reps}</p>
                    <p className="exercise-explanation"><strong>Explicación:</strong> {exercise.explanation}</p>
                  </div>
                  {exercise.videoUrl && (
                    <div className="exercise-video">
                      <iframe
                        width="100%"
                        height="300"
                        src={exercise.videoUrl}
                        title={exercise.name}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen>
                      </iframe>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="routine-footer">
            <p className="routine-price">{selectedRoutine.price}</p>
            <a href="https://wa.me/542983558723" target="_blank" rel="noopener noreferrer" className="buy-button">Contactar para Comprar</a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <nav className="navbar">
          <div className="logo">profe.estebanpardiñas</div>
          <button className="hamburger" onClick={toggleMenu}>
            <span></span>
            <span></span>
            <span></span>
          </button>
          <ul className={`nav-links ${menuOpen ? 'active' : ''}`}>
            <li><a href="#home" onClick={closeMenu}>Inicio</a></li>
            <li><a href="#about" onClick={closeMenu}>Sobre el Programa</a></li>
            <li><a href="#destacados" onClick={closeMenu}>Destacados</a></li>
            <li><a href="#features" onClick={closeMenu}>¿Por qué Elegirnos?</a></li>
            <li><a href="#contact" onClick={closeMenu}>Contacto</a></li>
          </ul>
        </nav>
      </header>

      <main>
        <section id="home" className="hero">
          {/* Animated background canvas effect */}
          <div className="hero-bg-blur"></div>
          
          {/* Content with split animations */}
          <div className="hero-wrapper">
            <div className="hero-content">
              <div className="hero-top-text">
                <span className="hero-badge">🏋️ Entrenamiento Personalizado</span>
              </div>
              
              <div className="hero-main-title">
                <h1 className="split-chars">
                  <span>L</span><span>a</span> <span>F</span><span>u</span><span>e</span><span>r</span><span>z</span><span>a</span>
                </h1>
                <h1 className="split-chars second-line">
                  <span>C</span><span>u</span><span>r</span><span>a</span>
                </h1>
              </div>
              
              <p className="hero-subtitle">Entrenamientos pensados para mejorar tu rendimiento, fuerza y movilidad</p>
              
              <div className="hero-cta-group">
                <a href="#destacados" className="cta-button primary">Explorar Rutinas</a>
                <a href="#about" className="cta-button secondary">Más Información</a>
              </div>
            </div>
          </div>
          
          {/* Animated elements */}
          <div className="hero-elements">
            <div className="element-circle e1"></div>
            <div className="element-circle e2"></div>
            <div className="element-circle e3"></div>
          </div>
          
          {/* Scroll indicator with animation */}
          <div className="hero-scroll">
            <svg viewBox="0 0 24 24" className="scroll-icon">
              <path d="M7 10l5 5 5-5z" />
            </svg>
            <span>Scroll</span>
          </div>
        </section>

        <section id="about" className="about about-with-bg">
          <div className="about-overlay"></div>
          <div className="about-content">
            <h2>Sobre el Programa</h2>
            <div className="about-grid">
              <div className="about-card">
                <div className="card-number">01</div>
                <h3>Adaptado a Tu Nivel</h3>
                <p>Rutinas diseñadas para principiantes, intermedios y avanzados. Cada programa se ajusta a tus capacidades y objetivos específicos.</p>
              </div>
              <div className="about-card">
                <div className="card-number">02</div>
                <h3>Entrenar con Sentido</h3>
                <p>Cada ejercicio tiene un propósito. Mejora tu rendimiento, fuerza y movilidad respetando tu cuerpo y evitando lesiones.</p>
              </div>
              <div className="about-card">
                <div className="card-number">03</div>
                <h3>Progreso Real</h3>
                <p>Priorizamos la constancia sobre la intensidad. Con disciplina y enfoque, lograrás resultados sostenibles en el tiempo.</p>
              </div>
            </div>
            <div className="about-highlight">
              <p>En cada programa de entrenamiento vas a encontrar rutinas pensadas para mejorar tu rendimiento, tu fuerza y tu movilidad, adaptadas a tu nivel y a tus objetivos. El enfoque está puesto en entrenar con sentido, respetando el cuerpo y priorizando la constancia para que el progreso sea real y sostenible en el tiempo.</p>
            </div>
          </div>
        </section>

        <section id="destacados" className="destacados">
          <h2>Destacados</h2>
          <div className="products-grid">
            {[
              { key: 'hombres-principiante', badge: 'Principiante', title: 'RUTINA PARA HOMBRES - NIVEL PRINCIPIANTE (4 SEMANAS)', price: '$50.000,00' },
              { key: 'mujeres-principiante', badge: 'Principiante', title: 'RUTINA PARA MUJERES - NIVEL PRINCIPIANTE (4 SEMANAS)', price: '$50.000,00' },
              { key: 'mujeres-intermedio', badge: 'Intermedio', title: 'RUTINA PARA MUJERES - NIVEL INTERMEDIO (4 SEMANAS)', price: '$50.000,00' },
              { key: 'hombres-intermedio', badge: 'Intermedio', title: 'RUTINA PARA HOMBRES - NIVEL INTERMEDIO (4 SEMANAS)', price: '$50.000,00' },
              { key: 'hombres-avanzado', badge: 'Avanzado', title: 'RUTINA PARA HOMBRES - NIVEL AVANZADO (4 SEMANAS)', price: '$50.000,00' },
              { key: 'mujeres-avanzado', badge: 'Avanzado', title: 'RUTINA PARA MUJERES - NIVEL AVANZADO (4 SEMANAS)', price: '$50.000,00' },
              { key: 'lesion-rodilla', badge: 'Especializado', title: 'RUTINA ENTRENAMIENTO - LESIONES DE RODILLA', price: '$60.000,00' },
              { key: 'dolor-lumbar', badge: 'Especializado', title: 'RUTINA DE ENTRENAMIENTO - DOLOR LUMBAR', price: '$60.000,00' }
            ].map(item => (
              <div 
                key={item.key}
                className="product-card"
                onClick={() => setSelectedRoutine(routineDetails[item.key])}
                onMouseEnter={(e) => handleCardHover(e, true)}
                onMouseLeave={(e) => handleCardHover(e, false)}
              >
                <div className="product-badge">{item.badge}</div>
                <div className="product-image"></div>
                <h3>{item.title}</h3>
                <div className="product-features">
                  <span className="feature">4 semanas</span>
                </div>
                <p className="product-price">{item.price}</p>
                <a 
                  href="https://wa.me/542983558723" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="buy-button"
                  onClick={(e) => e.stopPropagation()}
                  onMouseEnter={(e) => handleButtonHover(e, true)}
                  onMouseLeave={(e) => handleButtonHover(e, false)}
                >Contactar</a>
              </div>
            ))}
          </div>
        </section>

        <section id="features" className="features features-with-bg">
          <div className="features-overlay"></div>
          <div className="features-container">
            <div className="features-header">
              <h2>¿Por qué Elegirnos?</h2>
              <p className="features-subtitle">Características que nos hacen diferentes</p>
            </div>
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-number">01</div>
                <h3>Seguimiento Detallado</h3>
                <p>Monitorea tu progreso con análisis completos de cada sesión y visualiza tu evolución</p>
              </div>
              <div className="feature-card">
                <div className="feature-number">02</div>
                <h3>Rutinas Optimizadas</h3>
                <p>Diseñadas por expertos para máximo rendimiento y resultados comprobados</p>
              </div>
              <div className="feature-card">
                <div className="feature-number">03</div>
                <h3>Objetivos Claros</h3>
                <p>Alcanza tus metas con planes estructurados y seguimiento profesional</p>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="contact">
          <div className="contact-content">
            <h2>Contacto</h2>
            <p className="contact-subtitle">¿Tienes preguntas? Estamos aquí para ayudarte</p>
            <form className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Nombre Completo</label>
                <input type="text" id="name" placeholder="Tu nombre" required />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input type="email" id="email" placeholder="tu@email.com" required />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Teléfono (opcional)</label>
                <input type="tel" id="phone" placeholder="+54 9 11 2345-6789" />
              </div>
              <div className="form-group">
                <label htmlFor="message">Mensaje</label>
                <textarea id="message" placeholder="Cuéntanos qué necesitas..." rows="5"></textarea>
              </div>
              <button 
                type="submit" 
                className="submit-button"
                onMouseEnter={(e) => handleButtonHover(e, true)}
                onMouseLeave={(e) => handleButtonHover(e, false)}
              >Enviar Mensaje</button>
            </form>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>Contacto</h3>
            <p>
              <a href="https://wa.me/542983558723" target="_blank" rel="noopener noreferrer">
                WhatsApp: +54 9 2983 55-8723
              </a>
            </p>
          </div>

          <div className="footer-section">
            <h3>Sígueme</h3>
            <div className="social-links">
              <a href="https://web.facebook.com/esteban.pardinas" target="_blank" rel="noopener noreferrer" className="social-icon" title="Facebook">
                <Facebook size={24} />
              </a>
              <a href="https://www.instagram.com/profe.estebanpardinas?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer" className="social-icon" title="Instagram">
                <Instagram size={24} />
              </a>
            </div>
          </div>

          <div className="footer-section">
            <h3>Desarrollado por</h3>
            <div className="developer-credit">
              <a href="https://portafolio-vlobos.web.app/" target="_blank" rel="noopener noreferrer" className="developer-logo-link">
                <img src={process.env.PUBLIC_URL + "/img/designer-logo.png"} alt="Logo Diseñador" className="developer-logo" />
              </a>
              <p>Design & Development</p>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 profe.estebanpardiñas. Todos los derechos reservados.</p>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a 
        href="https://wa.me/542983558723?text=Hola%20profe%20Esteban%20Pardiñas" 
        className="whatsapp-button"
        target="_blank"
        rel="noopener noreferrer"
        title="Contactanos por WhatsApp"
      >
        <img src="/img/wsp.jpg" alt="WhatsApp" className="whatsapp-icon" />
      </a>
    </div>
  );
}

export default App;
